/**
 * Detects which "spelling variant abbreviation" (if any) appears in the
 * raw submitted text. Used by the dev spam-debug summary to show a
 * human-friendly label like "NuLu" or "J-Town" next to each decision,
 * regardless of whether the submission was blocked or allowed.
 *
 * Detection runs against the RAW text (not the normalized form) so the
 * label reflects what the user actually typed. Order matters: the first
 * match wins, so list more-specific patterns before broader ones.
 */
export type VariantMatch = {
  /** Canonical display label (what we show in the dev panel). */
  label: string;
  /** The exact substring the user typed that triggered the match. */
  matched: string;
};

type VariantRule = { label: string; pattern: RegExp };

// Keep these aligned with the variant lines in WHITELIST_PATTERNS and the
// VARIANT_CATALOG used by /admin/spam-variants. New variants added there
// should also be listed here so the dev panel can label them.
const VARIANT_RULES: VariantRule[] = [
  { label: "St. Matthews", pattern: /\b(?:saint|st\.?)[\s.]*matthews\b/i },
  { label: "Mt. Washington", pattern: /\b(?:mount|mt\.?)[\s.]*washington\b/i },
  { label: "Ft. Knox", pattern: /\b(?:fort|ft\.?)[\s.]*knox\b/i },
  { label: "LaGrange", pattern: /\bla[\s-]?grange\b/i },
  { label: "J-Town", pattern: /\bj[\s.\-]?town\b/i },
  { label: "Cox's Creek", pattern: /\bcox'?s[\s-]?creek\b/i },
  { label: "PRP", pattern: /\bp\.?r\.?p\.?\b/i },
  { label: "NuLu", pattern: /\bnu[\s-]?lu\b/i },
  { label: "Etown", pattern: /\be[\s-]?town\b/i },
  { label: "Fern Creek", pattern: /\bfern[\s-]?creek\b/i },
  { label: "Valley Station", pattern: /\bvalley[\s-]?station\b/i },
  { label: "Pleasure Ridge Park", pattern: /\bpleasure[\s-]?ridge[\s-]?park\b/i },
  { label: "New Albany", pattern: /\bnew[\s-]?albany\b/i },
  { label: "Jeffersonville (Jeff)", pattern: /\bjeff(?:ersonville)?\b/i },
  // Metro/regional shorthand
  { label: "Derby City", pattern: /\bderby city\b/i },
  { label: "Kentuckiana", pattern: /\bkentuckiana\b/i },
  { label: "SoIN / So. Indiana", pattern: /\b(?:so\.?\s*ind(?:iana)?|s\.?\s*indiana|southern ind|so[\s-]?in)\b/i },
  { label: "Greater/Metro Louisville", pattern: /\b(?:greater louisville|louisville metro|metro louisville)\b/i },
];

export function detectVariantLabel(text: string): VariantMatch | undefined {
  if (!text) return undefined;
  for (const { label, pattern } of VARIANT_RULES) {
    const m = text.match(pattern);
    if (m) return { label, matched: m[0] };
  }
  return undefined;
}

/** Exposed for tests + the admin variants page if it ever wants to reuse. */
export const __VARIANT_RULES_FOR_TEST = VARIANT_RULES;
